import { Box } from "@chakra-ui/react"
import { ServerBlockTypeType } from "@pathwright/blocks-core"
import {
  BlockType,
  EDITOR_MODE,
  scopeContentData,
  useEditorStateSelector
} from "@pathwright/blocks-engine"
import classnames from "classnames"
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react"
import styled from "styled-components"
import BlockContainer from "../block/BlockContainer"
import { BlockEditorContextProvider } from "./BlockEditorContext"

const BlockEditorContainer = ({ children }: { children: React.ReactNode }) => {
  return (
    <Box
      width="100%"
      position="relative"
      minHeight="30px"
      css={{
        "& .BlocksControls": {
          display: "none"
        }
      }}
      _hover={{
        "& .BlocksControls": {
          display: "flex"
        }
      }}
    >
      {children}
    </Box>
  )
}

const BlockEditorComponentContainer = styled.div`
  flex-grow: 1;
  width: 100%;
`

const BlockEditorWrap = styled.div`
  position: relative;
`

/**
 * Hook to manage block editor active state based on viewport visibility.
 * "Active" can be interepreted by Edit Block components as desired,
 * but the main goal is to prevent Quill Editors from being active until needed.
 * Blocks are activated when they enter the viewport (with a minimum time threshold)
 * or when clicked.
 */
const useBlockEditorActive = (elementRef: React.RefObject<HTMLDivElement>) => {
  const mode = useEditorStateSelector((state) => state.mode)
  const [isActive, setIsActive] = useState(false)
  const activationTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Minimum time a block must be in view before activating (prevents rapid activation during fast scrolling)
  const MIN_TIME_IN_VIEW_MS = 120

  // Instantly activate block for editing when clicked
  const handleMouseDown = useCallback(() => {
    if (mode === EDITOR_MODE.VIEW) return

    // Clear any pending activation timeout
    if (activationTimeoutRef.current) {
      clearTimeout(activationTimeoutRef.current)
      activationTimeoutRef.current = null
    }

    setIsActive(true)
  }, [mode])

  // Use IntersectionObserver to detect when block enters/leaves viewport
  useEffect(() => {
    if (mode === EDITOR_MODE.VIEW || !elementRef.current) return

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        const isIntersecting = entry.isIntersecting

        // Clear any pending activation timeout
        if (activationTimeoutRef.current) {
          clearTimeout(activationTimeoutRef.current)
          activationTimeoutRef.current = null
        }

        if (isIntersecting) {
          // Block entered viewport - wait for minimum time before activating
          activationTimeoutRef.current = setTimeout(() => {
            setIsActive(true)
            activationTimeoutRef.current = null
          }, MIN_TIME_IN_VIEW_MS)
        } else {
          // Block left viewport - deactivate immediately
          setIsActive(false)
        }
      },
      {
        // Trigger when block is partially visible (threshold: 0 means any visibility)
        threshold: 0,
        // Small rootMargin to trigger slightly before entering viewport
        rootMargin: "150px"
      }
    )

    observer.observe(elementRef.current)

    return () => {
      observer.disconnect()
      // Clear timeout on unmount
      if (activationTimeoutRef.current) {
        clearTimeout(activationTimeoutRef.current)
        activationTimeoutRef.current = null
      }
      setIsActive(false)
    }
  }, [mode, elementRef])

  return { isActive, handleMouseDown }
}

interface UseBlockEditorDataOptions {
  blockId: string
  propsData: Record<string, any>
  blockType: ServerBlockTypeType
  onChange: (data: Record<string, any>) => void
  debounceDelayMs?: number
}

interface BlockEditorProps {
  blockIndex: number
  blockType: ServerBlockTypeType
  block: BlockType
  onChange: (data: Record<string, any>) => void
}

const propsAreEqual = (
  prevProps: BlockEditorProps,
  nextProps: BlockEditorProps
) => {
  if (nextProps.blockIndex !== prevProps.blockIndex) return false
  if (nextProps.blockType !== prevProps.blockType) return false
  if (nextProps.block !== prevProps.block) return false

  return true
}

const BlockEditor = memo((props: BlockEditorProps) => {
  const { blockIndex, blockType, block: propsBlock, onChange } = props
  const { id, type, layout } = propsBlock
  const mode = useEditorStateSelector((state) => state.mode)

  // Ref for IntersectionObserver
  const elementRef = useRef<HTMLDivElement | null>(null)

  // Manage active state based on viewport visibility
  const { isActive, handleMouseDown } = useBlockEditorActive(elementRef)

  const data = propsBlock.data
  const handleChange = useCallback(
    (dataUpdates: Record<string, any>) => {
      const nextData = scopeContentData(
        { ...propsBlock.data, ...dataUpdates },
        blockType
      )
      onChange(nextData)
    },
    [propsBlock, blockType, onChange]
  )

  const className = classnames(
    "ContentBlock",
    `ContentBlockType-${type}`,
    `ContentBlockLayout-${layout}`
  )

  // Construct block object from props + local data state
  const block = useMemo(
    () => ({
      ...propsBlock,
      data
    }),
    [propsBlock, data]
  )

  if (mode === EDITOR_MODE.VIEW)
    return (
      <div className={className}>
        <BlockContainer
          key={`${propsBlock.contentID}:${id}`}
          onChange={handleChange}
          block={block}
        />
      </div>
    )

  return (
    <BlockEditorContextProvider isActive={isActive} block={block}>
      <BlockEditorWrap
        ref={elementRef}
        className={className}
        onMouseDown={handleMouseDown}
      >
        <BlockEditorContainer>
          <BlockEditorComponentContainer>
            <BlockContainer
              key={`${propsBlock.contentID}:${id}`}
              onChange={handleChange}
              blockIndex={blockIndex}
              block={block}
            />
          </BlockEditorComponentContainer>
        </BlockEditorContainer>
      </BlockEditorWrap>
    </BlockEditorContextProvider>
  )
}, propsAreEqual)

export default BlockEditor
